import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { Reducer } from "redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import { sessionSlice, sessionReducer } from "./slices/session.slice";
import { cartSlice, cartReducer } from "./slices/cart.slice";
import { locationApi } from "./apis/location.api";
import { authApi } from "./apis/auth.api";
import { verificationCodeApi } from "./apis/verification-code.api";
import { restaurantApi } from "./apis/restaurant.api";
import { itemApi } from "./apis/item.api";
import { orderApi } from "./apis/order.api";
import { restaurantRequestApi } from "./apis/restaurant-request.api";
import { riderRequestApi } from "./apis/rider-request.api";
import { adApi } from "./apis/ad.api";
import { userApi } from "./apis/user.api";

const reducers = {
  [sessionSlice.name]: sessionReducer,
  [cartSlice.name]: cartReducer,
  [locationApi.reducerPath]: locationApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [verificationCodeApi.reducerPath]: verificationCodeApi.reducer,
  [restaurantApi.reducerPath]: restaurantApi.reducer,
  [itemApi.reducerPath]: itemApi.reducer,
  [orderApi.reducerPath]: orderApi.reducer,
  [restaurantRequestApi.reducerPath]: restaurantRequestApi.reducer,
  [riderRequestApi.reducerPath]: riderRequestApi.reducer,
  [adApi.reducerPath]: adApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
};

const combinedReducer = combineReducers<typeof reducers>(reducers);

export const rootReducer: Reducer<RootState> = (state, action) => {
  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      locationApi.middleware,
      authApi.middleware,
      verificationCodeApi.middleware,
      restaurantApi.middleware,
      itemApi.middleware,
      orderApi.middleware,
      restaurantRequestApi.middleware,
      riderRequestApi.middleware,
      adApi.middleware,
      userApi.middleware,
    ]);
  },
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof combinedReducer>;
export const useTypedDispatch = () => useDispatch<AppDispatch>();
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

export {
  logout,
  setUser,
  setAddress,
  setOrderType,
} from "./slices/session.slice";

export {
  clear,
  addItem,
  removeItem,
  reduceItemQty,
  addItemQty,
} from "./slices/cart.slice";

export { useFetchAddressSuggestinsQuery } from "./apis/location.api";

export {
  useRegisterMutation,
  useLoginMutation,
  useResetPasswordMutation,
} from "./apis/auth.api";

export {
  useVerifyMutation,
  useGenerateMutation,
} from "./apis/verification-code.api";

export {
  useFetchRestaurantsSuggestionsQuery,
  useFetchNearbyRestaurantsQuery,
} from "./apis/restaurant.api";

export {
  useFetchItemsByRestaurantQuery,
  useFetchItemDetailsQuery,
} from "./apis/item.api";

export {
  useFetchOrderFeesQuery,
  useCheckoutMutation,
  useConfirmPaymentMutation,
  useFetchOrderDetailsQuery,
  useFetchCurrentOrdersCountQuery,
  useFetchCurrentOrdersQuery,
} from "./apis/order.api";

export { useCreateRestaurantRequestMutation } from "./apis/restaurant-request.api";

export { useCreateRiderRequestMutation } from "./apis/rider-request.api";

export { useFetchAdsByAddressQuery } from "./apis/ad.api";

export {
  useGetUserInfoQuery,
  useGetAddressesQuery,
  useAddAddressMutation,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
  useSetDefaultAddressMutation,
  useUpdateProfileMutation,
  useChangePasswordMutation,
} from "./apis/user.api";
